import * as R from 'ramda';
import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { useGetOrderSummary } from '../../../services/modules/order/order.queries';
import { GetOrderSummaryOutput } from '../../../services/modules/order/order.types';
import useOrderBookStore from '../../../stores/OrderBook/orderBook.store';
import { WithId } from '../../../types/Base.types';
import { getEnabledQueryBasedOnOfflineMode } from '../../offline/queryClient/utils/offlineDataQuery.utils';
import { UsedInOfflineMode } from '../../offline/types/UsedInOfflineMode';
import useCalculateSummary from '../hooks/useCalculateSummary';
import { ConditionSummary } from '../products/orderProducts.types';
import { getOrderCalculateUrlParams } from '../utils/orderCalculate.utils';
import { useOrderContext } from './Order.context';

const OrderCalculateContext = createContext<OrderCalculateContextProps>({
  summaryTotals: {} as GetOrderSummaryOutput,
  calculateSummary: async () => {
    return;
  },
  getConditionTotals: () => undefined,
});

interface OrderCalculateContextProviderProps extends UsedInOfflineMode {
  children: React.ReactNode;
}
export const OrderCalculateContextProvider = ({
  children,
  isOfflineMode,
}: OrderCalculateContextProviderProps) => {
  const params = useParams<WithId>();
  const location = useLocation();
  const { order } = useOrderContext();
  const { calculateSummary } = useCalculateSummary();
  const { selectedOrderId, updateSelectedOrderSummary } = useOrderBookStore(
    useShallow((state) => ({
      selectedOrderId: state.selectedOrder?.id,
      updateSelectedOrderSummary: state.updateSelectedOrderSummary,
    })),
  );

  const orderCalculateParams = useMemo(
    () => getOrderCalculateUrlParams(location.search),
    [location.search],
  );

  const { data: summaryData } = useGetOrderSummary(
    {
      isOfflineMode,
      orderId: params.id,
      typedParams: orderCalculateParams?.typedParams,
      urlParams: orderCalculateParams?.urlParams?.toString(),
    },
    { enabled: getEnabledQueryBasedOnOfflineMode(isOfflineMode) },
  );

  const getConditionTotals = useCallback(
    (orderConditionId: string) =>
      summaryData?.calculate.conditions?.find((condition) => condition.id === orderConditionId),
    [summaryData?.calculate.conditions],
  );

  useEffect(() => {
    const summaryTotal = summaryData?.calculate.total;
    if (selectedOrderId === order?.id && !R.isNil(summaryTotal)) {
      const { total, subtotal, pieces } = summaryTotal;
      updateSelectedOrderSummary({ total, subtotal, pieces });
    }
  }, [summaryData?.calculate.total]);

  const value: OrderCalculateContextProps = useMemo(
    () => ({
      summaryTotals: summaryData,
      calculateSummary,
      getConditionTotals,
    }),
    [calculateSummary, getConditionTotals, summaryData],
  );

  return <OrderCalculateContext.Provider value={value}>{children}</OrderCalculateContext.Provider>;
};

interface OrderCalculateContextProps {
  summaryTotals?: GetOrderSummaryOutput;
  calculateSummary: () => Promise<void>;
  getConditionTotals: (orderType: string) => ConditionSummary | undefined;
}

export const useOrderCalculateContext = () =>
  useContext<OrderCalculateContextProps>(OrderCalculateContext);
